import type { BlogArticle, SiteContent } from './beshrmakki-content';

export const beshrmakkiContentEn = {
  brand: 'Dr. Beshr Makki',
  nav: {
    home: { label: 'Home', href: '/' },
    services: { label: 'Services', href: '/services' },
    about: { label: 'About', href: '/#about' },
    articles: { label: 'Articles', href: '/blog' },
    reviews: { label: 'Patient Reviews', href: '/reviews' },
    contact: { label: 'Contact', href: '/contact' },
    cta: { label: 'Book Appointment', href: '/contact' },
  },
  featured: {
    eyebrow: 'Lab Results Specialist',
    title: 'Understanding your results starts here',
    cta: 'Explore our services',
  },
  hero: {
    headline: 'Your body speaks in codes… because a lab result is more than a number on paper',
    intro:
      'Specialized in interpreting medical lab results, turning complex data into clear information that helps you make confident health decisions.',
    subtext:
      'Our goal is not only to deliver results, but to transform complex data into clear insights that give you peace of mind and the ability to make the right health decisions.',
    badges: ['100% Specialist Accuracy', 'Privacy & Security'],
    cta: { label: 'Book a consultation or contact us now', href: '/contact' },
  },
  about: {
    title: 'Who is Dr. Beshr Makki?',
    description:
      'Dr. Beshr Makki does not see the laboratory as mere test tubes, but as a gateway to understanding human health. As a researcher and practitioner, he dedicates his time to decoding medical results.',
    pillars: [
      {
        title: 'Certified Interpreter',
        description: 'Translating complex medical terms into simple language patients can understand',
      },
      {
        title: 'Scientific Precision',
        description: 'Focusing on overlapping and precise causes when reviewing results',
      },
      {
        title: 'Myth Correction',
        description: 'Shedding light on common medical myths about various lab tests',
      },
    ],
    link: { label: 'Learn more about Dr. Beshr Makki', href: '/#about' },
  },
  values: {
    eyebrow: 'Our Values',
    title: 'Why do patients trust Dr. Beshr Makki?',
    subtitle: 'Our values are our guide — get to know them',
    items: [
      {
        title: 'Scientific Integrity',
        description:
          'Delivering the full medical truth without exaggeration or minimization, with commitment to the medical oath.',
      },
      {
        title: 'Extreme Precision',
        description: 'Examining every small detail in the report to ensure no important health indicator is missed.',
      },
      {
        title: 'Reliability',
        description: 'A strict workflow that ensures accuracy and consistency of results under any circumstances.',
      },
    ],
  },
  testimonials: {
    title: 'What do patients say about us?',
    items: [
      {
        quote:
          'I was lost between labs until Dr. Beshr explained my results with amazing simplicity. Now I know exactly what my next step is.',
        author: 'Ahmed M.',
        role: 'Chronic diabetes patient',
      },
      {
        quote:
          'Precision and honesty are what distinguish this place. I felt psychological comfort as soon as I understood every number in my medical report.',
        author: 'Sarah K.',
        role: 'Routine follow-up',
      },
    ],
  },
  articles: {
    eyebrow: 'Current Topics',
    title: 'Topics we discuss',
    subtitle: 'Health awareness starts with the right information',
    viewAll: { label: 'View all articles and videos', href: '/blog' },
    items: [
      {
        title: 'How to choose the right laboratory?',
        category: 'Patient Guide',
        excerpt: 'Quality standards to look for before any important medical test.',
      },
      {
        title: 'Myths about lab results',
        category: 'Medical Culture',
        excerpt: 'Why a slight increase does not always mean disease? Surprising scientific facts.',
      },
      {
        title: 'Reading complex symbols',
        category: 'Results Interpretation',
        excerpt: 'A simplified explanation of the most alarming medical symbols in reports.',
      },
      {
        title: 'Before the test… what to do?',
        category: 'Tips',
        excerpt: 'Simple steps that ensure accurate results and save you from retesting.',
      },
    ],
  },
  audience: {
    eyebrow: 'A great fit for',
    title: 'Dr. Beshr Makki is right for',
    items: [
      'Those seeking accurate and reliable medical opinions.',
      'Families who want comprehensive routine health monitoring.',
      'Chronic patients who need interpretation of their condition changes.',
      'Anyone anxious about an unclear lab result.',
    ],
    cta: { label: 'Explore our services', href: '/services' },
  },
  mission: {
    title: 'Our Mission',
    slogan: 'Testing is life',
    description:
      'We believe every drop of blood tells a story, and our mission is to read that story honestly to give you a healthier and brighter tomorrow.',
  },
  newsletter: {
    title: 'Join the health awareness community',
    subtitle: 'Get the latest medical tips and lab interpretations in your inbox',
    instruction: 'Enter your details and click subscribe',
    namePlaceholder: 'Name',
    emailPlaceholder: 'Email address',
    button: 'Subscribe now',
    success: 'Thank you for subscribing! We will send you the latest tips soon.',
  },
  quote: {
    text: 'The journey begins with a word… and I am here to tell you the right word',
    attribution: 'Dr. Beshr Makki — Your body\'s code translator',
  },
  footer: {
    aboutTitle: 'About the Doctor',
    aboutDescription:
      'Specialized in interpreting medical lab results with scientific accuracy and integrity, turning complex numbers into clear insights that give you peace of mind.',
    legalTitle: 'Legal Information',
    quickLinks: [
      { label: 'Who is Dr. Beshr Makki?', href: '/#about' },
      { label: 'Services', href: '/services' },
      { label: 'Articles', href: '/blog' },
      { label: 'Book appointment', href: '/contact' },
    ],
    copyright: '© 2026 Dr. Beshr Makki — All rights reserved. Specialist in precise medical lab analysis',
    links: [
      { label: 'Medical Disclaimer', href: '/privacy' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Use', href: '/terms' },
      { label: 'Contact us', href: '/contact' },
    ],
    social: {
      facebook: 'https://www.facebook.com',
      instagram: 'https://www.instagram.com',
      twitter: 'https://www.twitter.com',
      youtube: 'https://www.youtube.com',
      whatsapp: 'https://wa.me/',
    },
  },
  servicesPage: {
    heroTitle: 'Dr. Beshr Makki Services',
    heroDescription:
      'We provide specialized services in interpreting medical lab results, turning complex numbers into clear information that helps you make confident health decisions.',
    pillarsLabel: 'Our Services',
    serviceCardCta: 'Book consultation',
    ctaHeading: 'Have a lab report that needs interpretation?',
    ctaDescription:
      'Book your consultation now with Dr. Beshr Makki and get a clear, simplified explanation of your results.',
    ctaButtonText: 'Book appointment',
    methodologyLabel: 'Our Methodology',
    methodologyHeading: 'How do we work with you?',
    phases: [
      {
        title: 'Report Intake',
        description:
          'We carefully review the lab report and gather all relevant indicators and reference values for your condition.',
      },
      {
        title: 'Simplified Interpretation',
        description:
          'We translate medical terms and numbers into clear language you and your family understand — without exaggeration or minimization.',
      },
      {
        title: 'Follow-up Plan',
        description:
          'We provide practical recommendations for next steps: follow-up, additional tests, or medical consultation when needed.',
      },
    ],
    caseStudiesText: 'Read our awareness articles',
    caseStudiesHref: '/blog',
    items: [
      {
        title: 'Medical Lab Interpretation',
        description: 'Comprehensive explanation of blood, urine, and lab test results in simple, accurate language.',
      },
      {
        title: 'Periodic Report Review',
        description: 'Tracking your health indicators over time and linking results to your medical context.',
      },
      {
        title: 'Pre-test Consultation',
        description: 'Guiding you on proper test preparation to ensure accurate results the first time.',
      },
      {
        title: 'Awareness & Myth Correction',
        description: 'Spreading health awareness and correcting common myths about various lab results.',
      },
    ],
  },
  contactPage: {
    pageTitle: 'Contact Us',
    pageDescription:
      'Book your consultation or send your inquiry about your lab results. We are here to help you understand your medical report clearly.',
    emailLabel: 'Email',
    emailValue: 'info@beshrmakki.com',
    phoneLabel: 'Phone',
    phoneValue: '',
    locationLabel: 'Location',
    locationValue: 'Online consultations and clinic',
    formNameLabel: 'Name',
    formEmailLabel: 'Email',
    formSubjectLabel: 'Subject',
    formMessageLabel: 'Your message',
    formSubmitText: 'Send message',
    formSubmittingText: 'Sending...',
    formSuccessText: 'Your message was sent successfully. We will contact you soon.',
    formErrorText: 'Failed to send message.',
    profileBadge: 'Dr. Beshr Makki',
    baseOperationsLabel: 'Consultations',
    baseCity: 'Book your appointment now',
    baseDescription:
      'Whether it is your first visit or a routine follow-up, we ensure a clear and accurate explanation of every result in your report.',
    officeLabel: 'WhatsApp',
    directionsText: 'Direct contact',
    directionsUrl: 'https://wa.me/',
  },
  blogPage: {
    title: 'Articles & Health Awareness',
    subtitle: 'Health awareness starts with the right information',
    emptyMessage: 'No articles available at the moment.',
    readMore: 'Read full article',
    backToBlog: 'Back to articles',
    notFound: 'Article not found.',
    author: 'Dr. Beshr Makki',
  },
  privacyPage: {
    disclaimerTitle: 'Medical Disclaimer',
    disclaimerBody: `The information provided on this website is for educational and interpretive purposes only and does not replace direct medical consultation with a qualified physician.

No content on this site constitutes a medical diagnosis or prescription. Patients should consult their treating physician before making any treatment decision based on lab results.

Dr. Beshr Makki is not responsible for any use of published information without appropriate medical consultation.`,
    privacyTitle: 'Privacy Policy',
    privacyBody: `We respect your privacy and are committed to protecting your personal data according to best practices.

We only collect data you voluntarily provide through contact forms or newsletter subscription, and use it to respond to your inquiries and provide requested services.

We do not share your data with third parties except when legally required or with your explicit consent.

You may request deletion or modification of your data by contacting us through the contact page.`,
  },
  termsPage: {
    title: 'Terms of Use',
    body: `By using this website, you agree to the following terms:

Content is provided for informational and interpretive purposes only and does not constitute a doctor-patient relationship except after completing an official consultation.

Copying or republishing site content without prior written permission is prohibited.

We reserve the right to modify these terms at any time. Continued use of the site constitutes acceptance of the modifications.

For any inquiry about these terms, please contact us through the contact page.`,
  },
  reviewsPage: {
    title: 'What do patients say about us?',
    subtitle: 'Real reviews from patients who benefited from lab interpretation services',
  },
} as const satisfies SiteContent;

export const blogArticlesEn: BlogArticle[] = [
  {
    id: 1,
    title: 'How to choose the right laboratory?',
    category: 'Patient Guide',
    excerpt: 'Quality standards to look for before any important medical test.',
    content: `Choosing the right laboratory is a fundamental step to ensure accurate results. Look for an accredited, licensed lab and verify quality certifications such as ISO.

Ask about sample collection, storage, and transport methods, as any flaw in this stage may affect the result.

Compare laboratories by turnaround time, report clarity, and availability of consultation to interpret results when needed.

Do not choose a lab based on price alone; accuracy and reliability matter more than savings for sensitive tests.`,
    created_at: '2026-01-15',
  },
  {
    id: 2,
    title: 'Myths about lab results',
    category: 'Medical Culture',
    excerpt: 'Why a slight increase does not always mean disease? Surprising scientific facts.',
    content: `Many patients worry about any number outside the normal range, but reference limits are not a final verdict.

A slight increase may reflect temporary conditions such as stress, diet, or sample timing, and does not necessarily indicate disease.

Proper interpretation requires looking at the full picture: symptoms, medical history, and previous tests.

Do not rush to judge your health from a single number; consult a specialist who interprets the result in the context of your full condition.`,
    created_at: '2026-02-01',
  },
  {
    id: 3,
    title: 'Reading complex symbols',
    category: 'Results Interpretation',
    excerpt: 'A simplified explanation of the most alarming medical symbols in reports.',
    content: `Lab reports are full of symbols and abbreviations that may seem frightening: HbA1c, LDL, ALT, TSH, and more.

Each symbol represents a specific body function indicator. Understanding it starts with knowing what it measures, not just whether it is high or low.

What matters is not memorizing symbols, but understanding what the value means for your current health.

The specialist's role is to translate these symbols into understandable language: Do you need follow-up? Lifestyle changes? Or a physician visit?`,
    created_at: '2026-02-20',
  },
  {
    id: 4,
    title: 'Before the test… what to do?',
    category: 'Tips',
    excerpt: 'Simple steps that ensure accurate results and save you from retesting.',
    content: `Proper test preparation saves you from retesting and worry. Ask the lab about required fasting and duration before glucose and lipid tests.

Avoid intense physical effort and smoking for hours before sampling, and continue your usual medication unless your doctor advises otherwise.

Tell the technician about any medications or supplements you take, as some affect results.

Keep a clear list of lab instructions and do not hesitate to ask when anything is unclear; accuracy starts before the sample is taken.`,
    created_at: '2026-03-10',
  },
];
